import { isDefined } from '@nfctron/nexus-core';
import { UiButton, UiDrawer, UiDrawerContent, UiDrawerFooter, UiDrawerHeader, UiDrawerTitle, cn } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { LogInIcon } from 'lucide-react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { useLogin } from '@root/features/authentication/hooks/useLogin';
import { Form } from '@root/features/form/components/Form/Form';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { type LoginSheetFormValues, type LoginSheetProps } from './LoginSheet.types';

/**
 * LoginSheet component
 * @constructor
 */
export const LoginSheet: Component<LoginSheetProps> = ({ open, onClose, onSuccess, children, ...props }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, LoginSheet.displayName);
	const form = useForm<LoginSheetFormValues>({
		mode: 'all',
		criteriaMode: 'firstError',
	});

	/** use the custom useLogin hook */
	const loginMutation = useLogin();
	const isSubmitting = loginMutation.isPending;

	/** login submit handler */
	const onSubmitHandler: SubmitHandler<LoginSheetFormValues> = (values) => {
		loginMutation.mutate(values, {
			onSuccess: async () => {
				await onSuccess?.();
				onClose?.();
			},
			onError: (error: any) => {
				form.setError('password', {
					type: 'manual',
					message: t('form.password.errors.invalidCredentials.text'),
				});
				console.error('Login error:', error.message);
			},
		});
	};

	return (
		<UiDrawer open={open} onClose={onClose} handleOnly={isDefined(onClose) ? false : true} repositionInputs={false}>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
					<UiDrawerTitle asChild>
						<Text fw={700} c="lucky-point" fz={18}>
							{t('title')}
						</Text>
					</UiDrawerTitle>
				</UiDrawerHeader>

				<div className="flex flex-col gap-3 p-4">
					{/* children */}
					{children}

					{/* login form */}
					<Form
						className={cn('flex flex-col gap-3', isSubmitting && 'pointer-events-none opacity-50')}
						onSubmit={onSubmitHandler}
						form={form}
						withInvisibleSubmit
					>
						{/* email input */}
						<Form.TextInput<LoginSheetFormValues>
							fieldId="email"
							label={t('form.email.label')}
							radius="md"
							type="email"
							autoComplete="email"
							options={{
								required: t('form.email.errors.required.text'),
							}}
							placeholder={t('form.email.placeholder')}
						/>

						{/* password input */}
						<Form.PasswordInput<LoginSheetFormValues>
							fieldId="password"
							label={t('form.password.label')}
							autoComplete="current-password"
							options={{ required: t('form.password.errors.required.text') }}
							placeholder={t('form.password.placeholder')}
						/>
					</Form>
				</div>

				<UiDrawerFooter>
					<UiButton
						type="submit"
						size="lg"
						onClick={form.handleSubmit(onSubmitHandler)}
						variant="primary"
						loading={isSubmitting}
						fullWidth
						leftSection={<LogInIcon />}
					>
						{t('submitButton.text')}
					</UiButton>
				</UiDrawerFooter>
			</UiDrawerContent>
		</UiDrawer>
	);
};

/**
 * LoginSheet component default props
 * @type {ComponentDisplayName}
 */
LoginSheet.displayName = 'component/LoginSheet';
